import { useEffect, useState } from "react";

import { DollarOutlined, FrownOutlined } from "@ant-design/icons";
import { Skeleton, Divider } from "antd";
import { t } from "i18next";
import { useLocation, useParams } from "react-router-dom";

import {
  usePropertyOverviewFloorsMetadata,
  usePropertyOverviewFloorsStatuses,
  usePropertyOverviewFloorsWaterConsumption,
} from "../../../api/hooks/usePropertyOverview";
import WaterDropIcon from "../../../assets/icons/WaterDropIcon";
import AlertsModal from "../../Modals/AlertsModal";
import DevicesModal from "../../Modals/DevicesModal";
import SearchInput from "../../SearchInput/SearchInput";
import Water from "../../Shared/Water";
import Floor from "../Floor/Floor";

import "./FloorList.scss";

function FloorList() {
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const { property } = location.state || {};
  const { propertyId } = useParams();

  // Get PropertyOverview Floors Metadata.
  const { data: floors, status } = usePropertyOverviewFloorsMetadata(
    propertyId,
    {
      search: debouncedSearchQuery,
    },
  );

  // Get PropertyOverview Floors Water Consumption.
  const {
    data: floorsWaterConsumptionCollectionData,
    isLoading: floorsWaterConsumptionIsLoading,
  } = usePropertyOverviewFloorsWaterConsumption(propertyId);

  // Get PropertyOverview Floors Statuses
  const {
    data: floorsStatusesCollectionData,
    isLoading: floorsStatusesIsLoading,
  } = usePropertyOverviewFloorsStatuses(propertyId);

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  return (
    <div className="floor-list">
      <div className="flex flex-col-reverse sm:flex-row sm:items-center mb-4 md:mb-1 w-full">
        <div className="mt-6 sm:mt-0 flex-1 flex flex-col md:flex-row md:items-center mr-auto">
          <h1 className="page-title mb-0">{property?.Name}</h1>

          <div className="states md:ml-4">
            <div>
              <span className="square square--blue">&nbsp;</span>
              <span>{t("property_overview_page.neutral_state")}</span>
            </div>
            <div>
              <span className="square square--orange">&nbsp;</span>
              <span>{t("property_overview_page.negative_state")}</span>
            </div>
          </div>
        </div>

        <SearchInput
          className="ml-auto w-full sm:w-fit"
          placeholder={t("property_overview_page.search_floors")}
          disabled={
            (!searchQuery && floors?.length === 0) || status === "loading"
          }
          value={searchQuery}
          onChange={handleSearchChange}
        />
      </div>

      <div className="text-white text-xs text-opacity-80 font-light mb-4">
        <span className="text-14">{property?.Address || "-"}</span>
        <Divider type="vertical" className="mx-3" />

        <span className="text-14">
          {property?.SystemsCount || 0} {t("property_overview_page.hubs")}
        </span>
        <Divider type="vertical" className="ml-3" />

        <DevicesModal
          devicesCount={property?.DevicesCount}
          propertyId={property?.Id}
        />
        <Divider type="vertical" />

        <AlertsModal
          alertsCount={property?.AlertsCount}
          propertyId={property?.Id}
        />
        <Divider type="vertical" />

        <span>
          <WaterDropIcon className="mr-1 w-[0.6rem] align-sub" />
          <span className="mr-2">
            {t("property_overview_page.consumption")}
          </span>
          <Water value={property?.TotalWaterConsumption?.CurrentMonthValue} />
        </span>
        <Divider type="vertical" />

        <span>
          <DollarOutlined className="mr-1" />
          <span className="mr-2">{t("property_overview_page.savings")}</span>
          {property?.TotalWaterConsumption?.WaterSavingsPercentage || "-"}%
        </span>
      </div>

      {status === "loading" &&
        Array.from({ length: 3 }, (value, index) => index).map((el) => (
          <Skeleton
            active
            paragraph={{
              rows: 2,
            }}
            key={el}
          />
        ))}

      {status === "success" && floors?.length === 0 && (
        <div className="p-6 text-white text-opacity-80">
          <FrownOutlined className="text-xl mr-2" />
          {t("property_overview_page.no_matches")}
        </div>
      )}

      {status === "success" && floors?.length > 0 && (
        <div className="sm:grid sm:grid-cols-2 lg:grid-cols-1 gap-x-6 lg:gap-x-0">
          {floors
            ?.sort((firstElement, secondElement) =>
              firstElement.Name.localeCompare(secondElement.Name),
            )
            .map((floor) => (
              <Floor
                key={floor.Id}
                floor={floor}
                floorsWaterConsumptionCollectionData={
                  floorsWaterConsumptionCollectionData
                }
                floorsWaterConsumptionIsLoading={
                  floorsWaterConsumptionIsLoading
                }
                floorsOverviewStatusesCollectionData={
                  floorsStatusesCollectionData
                }
                floorsOverviewStatusesIsLoading={floorsStatusesIsLoading}
              />
            ))}
        </div>
      )}
    </div>
  );
}

export default FloorList;
